import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import TelegramWebApp from '../telegram-init';
import { referralService } from '../services/referral.service';
import type { ReferralStats } from '../types/referral';
import { ReferralStoreContext, type ReferralStoreContextValue } from './ReferralStoreContext';

const initialStats: ReferralStats = {
  current_rank: 'Bronze',
  total_referrals: 0,
  active_referrals: 0,
  total_earnings: 0,
  points_to_next_rank: 0,
  next_rank: 'Silver',
  referrals_by_level: {
    level1: 0,
    level2: 0,
    level3: 0
  },
  subscription_reward: 0,
  reward_claimed: false,
  subscription_purchased: false
};

interface ReferralStoreProviderProps {
  children: ReactNode;
}

export const ReferralStoreProvider: React.FC<ReferralStoreProviderProps> = ({ children }) => {
  const [referralStats, setReferralStats] = useState<ReferralStats>(initialStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadReferralStats = useCallback(async () => {
    const userId = TelegramWebApp.initDataUnsafe?.user?.id;
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      setError(null);
      console.log('[ReferralStore] Loading referral stats for user:', userId);
      const stats = await referralService.getReferralStats(userId.toString());
      console.log('[ReferralStore] Loaded referral stats:', stats);
      if (stats && typeof stats === 'object') {
        console.log('[ReferralStore] Setting referral stats:', stats);
        setReferralStats(prevStats => ({
          ...prevStats,
          ...stats
        }));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load referral stats');
      setError(error);
      console.error('[ReferralStore] Error loading referral stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadStats = useCallback(async () => {
    setLoading(true);
    await loadReferralStats();
  }, [loadReferralStats]);

  const copyReferralLink = useCallback(async (referralCode: string) => {
    try {
      await referralService.copyReferralLink(referralCode);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to copy referral link');
      setError(error);
      console.error('[ReferralStore] Failed to copy referral link:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    loadReferralStats();

    // Увеличиваем интервал до 2 минут и останавливаем когда страница не видна
    const handleVisibilityChange = () => {
      if (document.hidden) {
        return; // Не обновляем когда страница скрыта
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Обновляем только если страница видна
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        loadReferralStats();
      }
    }, 120000); // 2 минуты вместо 30 секунд

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadReferralStats]);

  const value: ReferralStoreContextValue = {
    referralStats,
    loading,
    error,
    reloadStats,
    copyReferralLink,
  };

  return (
    <ReferralStoreContext.Provider value={value}>
      {children}
    </ReferralStoreContext.Provider>
  );
};


